<template>
  <div v-if="hasPopulationToPopulationSettings" class="mt-16">
    <div
      v-for="(override, index) in populationToPopulationSettings"
      :key="override.partnerPopulation.id"
      class="pl-[50px] pr-16"
      :class="{
        'mb-16': index + 1 === populationToPopulationSettings.length,
      }"
    >
      <BittsDivider
        v-if="index > 0"
        class="my-16"
        :class="{ 'mt-0': index === 0 }"
      />
      <div class="flex justify-between items-center">
        <div class="flex items-center gap-8 text-neutral-text">
          <BittsSvg svg="cornerDownRight" class="text-neutral-text" />
          that are {{ partnerName }}'s {{ override.partnerPopulation.name }}
          <BittsTooltip>
            <FontAwesomeIcon :icon="['fak', 'information-circle']" />
            <template #title>
              <b>
                This override was created before the feature was deprecated.
              </b>
              <br />
              You can still view or delete existing overrides, but can't create
              new ones.
            </template>
          </BittsTooltip>
        </div>
        <BittsButton
          v-if="hasSharingPermission"
          @click="handleDeletePopulationToPopulationSetting(override)"
          variant="outline"
          type="neutral"
          size="x-small"
          class="p-6"
        >
          <template #icon>
            <FontAwesomeIcon
              :icon="['fak', 'x']"
              :style="{
                height: '12px',
                width: '12px',
                color: 'currentColor',
              }"
              class="text-danger-accent"
            />
          </template>
        </BittsButton>
      </div>
    </div>
    <BittsDivider v-if="!isLastItem" class="my-0" />
  </div>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsDivider,
  BittsSvg,
  BittsTooltip,
} from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed } from 'vue';

import useAuth from '@/composables/useAuth';
import usePopulations from '@/composables/usePopulations';

interface PopulationToPopulationSetting {
  partnerPopulation: {
    id: number;
    name: string;
  };
  dataShare: {
    id: number;
  };
}

const props = defineProps<{
  populationId: number;
  partnerName: string;
  isLastItem: boolean;
  partnerId: number;
  deleteOverride: (
    shareId: number,
    partnerPopulationId: number,
    populationId: number,
  ) => void;
  currentPopulationToPopulation: Record<number, number[]>;
}>();

const { hasPermission } = useAuth();
const hasSharingPermission = computed(() => hasPermission('manage:sharing'));

const { customPopsWithStatsAndSharing, allPopulations } = usePopulations(
  props.partnerId as never,
);

const populationToPopulationSettings = computed(() => {
  const population = [
    ...allPopulations.value,
    ...customPopsWithStatsAndSharing.value,
  ].find((population) => population.id === props.populationId);
  const settings = (population?.populationToPopulationSettings ||
    []) as PopulationToPopulationSetting[];

  // Filter out overrides that are not in currentPopulationToPopulation
  const currentIds =
    props.currentPopulationToPopulation[props.populationId] || [];
  return settings.filter((setting: PopulationToPopulationSetting) =>
    currentIds.includes(setting.partnerPopulation.id),
  );
});

const hasPopulationToPopulationSettings = computed(() => {
  return populationToPopulationSettings.value.length > 0;
});

function handleDeletePopulationToPopulationSetting(
  override: PopulationToPopulationSetting,
) {
  props.deleteOverride(
    override.dataShare.id,
    override.partnerPopulation.id,
    props.populationId,
  );
}
</script>
